import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Button, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../App.css';
import logo from '../../assets/logo.svg';
import { useNavigate, Link } from 'react-router-dom';
import { login } from '../../apis/login';
import Loader from '../loader/Loader';
import { loginSuccess, logout } from '../../store/slices/authSlice';
import AuthBackground from './AuthBackground';
import AuthContainer from './AuthContainer';
import ConsentBanner from '../ConsentBanner';

const validationSchema = Yup.object({
    username: Yup.string().required('Email is required'),
    password: Yup.string().min(8, 'Password must be at least 8 characters').required('Password is required'),
});

const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Auto-logout if user is already logged in (prevents back button access to dashboard)
    // Only check on component mount, not when user state changes
    useEffect(() => {
        // Check if user exists in localStorage (indicates they navigated here while logged in)
        const storedUser = localStorage.getItem('authUser');
        const storedToken = localStorage.getItem('authToken');
        
        if (storedUser || storedToken) {
            // User is logged in, logout them to ensure fresh login
            dispatch(logout());
            // Clear localStorage
            localStorage.removeItem('authUser');
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
        }
    }, [dispatch]); // Only run once on mount

    const formik = useFormik({
        initialValues: {
            username: '',
            password: '',
            loading: false,
            showPassword: false,
        },
        validationSchema,
        onSubmit: async (values) => {
            handleEmailLogin(values);
        },
    });

    const handleEmailLogin = async (values) => {
        try {
            formik.setFieldValue('loading', true);
            const data = await login({
                username: values.username,
                password: values.password,
            });
            dispatch(
                loginSuccess({
                    user: data.user,
                    tokens: data.tokens,
                    permissions: data?.permissions,
                    subscription: data?.subscription,
                })
            );
            navigate('/');
        } catch (error) {
            console.error('Login error:', error);
            let errorMessage = 'Invalid credentials. Please try again.';
            
            // Handle different error formats
            if (typeof error === 'string') {
                // Login API rejects with a string
                errorMessage = error;
            } else if (error?.errors?.non_field_errors?.[0]) {
                // Handle error object with errors.non_field_errors structure (direct API response)
                errorMessage = error.errors.non_field_errors[0];
            } else if (error?.response?.data?.errors?.non_field_errors?.[0]) {
                // Handle axios error response structure
                errorMessage = error.response.data.errors.non_field_errors[0];
            } else if (error?.response?.data?.message) {
                // Handle error with message field
                errorMessage = error.response.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            console.log('Error message:', errorMessage);
            // Ensure toast is displayed
            toast.error(errorMessage);
            console.log('Toast.error called');
        } finally {
            formik.setFieldValue('loading', false);
        }
    };

    const handleTogglePasswordVisibility = () => formik.setFieldValue('showPassword', !formik.values.showPassword);

    return (
        <div className="login_container min-vh-100">
            <AuthBackground />
            <div className="content_container">
                <Loader isLoading={formik.values.loading} />
                <div className="content_box">
                    <img src={logo} alt="Logo" className="logo" />
                    <Typography variant="body1" textAlign="center" marginTop={6} fontWeight={700}>Login to access your account</Typography>

                    <AuthContainer>
                        <form onSubmit={formik.handleSubmit}>
                            <div className="input_group mb-3">
                                <label className="input_label">Email</label>
                                <TextField
                                    name="username"
                                    type="text"
                                    placeholder="Enter your Email"
                                    value={formik.values.username}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.username && Boolean(formik.errors.username)}
                                    helperText={formik.touched.username && formik.errors.username}
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                />
                            </div>
                            <div className="input_group mb-4">
                                <label className="input_label">Password</label>
                                <TextField
                                    name="password"
                                    type={formik.values.showPassword ? 'text' : 'password'}
                                    placeholder="Enter your password"
                                    value={formik.values.password}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.password && Boolean(formik.errors.password)}
                                    helperText={formik.touched.password && formik.errors.password}
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={handleTogglePasswordVisibility} edge="end" size="small">
                                                    {formik.values.showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>

                            <div style={{ textAlign: 'right', marginBottom: '16px' }}>
                                <Link
                                    to="/forgot-password"
                                    style={{
                                        color: '#153d7c',
                                        textDecoration: 'none',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.textDecoration = 'underline';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.textDecoration = 'none';
                                    }}
                                >
                                    Forgot password?
                                </Link>
                            </div>

                            <Button type="submit" variant="contained" disabled={formik.isSubmitting} fullWidth>
                                Login
                            </Button>
                        </form>

                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                            <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                                Not registered yet?{' '}
                                <Link
                                    to="/signup"
                                    style={{
                                        color: '#153d7c',
                                        textDecoration: 'none',
                                        fontWeight: '500',
                                    }}
                                >
                                    Sign up here
                                </Link>
                            </p>
                        </div>
                    </AuthContainer>
                    <p className="copyright">© 2025-2026 Pratham Software. All rights reserved.</p>
                </div>
            </div>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                style={{ zIndex: 9999 }}
            />
            <ConsentBanner />
        </div>
    );
};

export default Login;
